/**
 * Session message loader (issue #3280) — body of the "load messages from server
 * when sessionId changes (conversation switch)" effect. The orchestrator keeps
 * the `useEffect` shell AND its `[sessionId, token]` dependency array.
 *
 * Web-internal module: must NOT be promoted to a shared `@xavior/*` package
 * (apps/web/AGENTS.md forbids web → domain/persistence/etc.).
 */
import type React from 'react'
import { getConversationChatMessages } from '@/lib/api/endpoints/chat'
import { serverToClientMessage } from '../utils/message-mapping'
import type { ChatMessage, QuickAction } from './useChat.types'

export interface SessionMessageLoaderDeps {
  sessionId: string | undefined
  token: string | null
  previousSessionIdRef: React.MutableRefObject<string | undefined>
  greetingAbortControllerRef: React.MutableRefObject<AbortController | null>
  greetingCancelledRef: React.MutableRefObject<boolean>
  greetingInProgressRef: React.MutableRefObject<boolean>
  bypassedNewConversationIdRef: React.MutableRefObject<string | null>
  lastCreatedConversationIdRef: React.MutableRefObject<string | null>
  clearLastCreatedConversationIdRef: React.MutableRefObject<() => void>
  currentSessionIdRef: React.MutableRefObject<string | undefined>
  // #5232: invoked when the messages GET for the (restored) conversation
  // fails. The recovery decides whether the failure means the conversation is
  // gone (HTTP 404) and, if so, clears the stale `xavior_last_conversation:<ws>`
  // pointer + triggers a conversations refetch so the app falls back to the
  // default conversation instead of proceeding with an empty dead thread
  // (which onboarding would then seed into). Optional so callers/tests that
  // don't wire recovery keep the historical log-and-empty behavior.
  recoverIfConversationGoneRef?: React.MutableRefObject<(err: unknown, conversationId: string) => boolean>
  // The canonical timeline reset — clears the activity timeline AND cancels any
  // pending min-display requeue timers, so a status scheduled during the prior
  // conversation's stream can't fire into the freshly-loaded one (#orphan-timer).
  clearActivity: () => void
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  setThinking: React.Dispatch<React.SetStateAction<string | null>>
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<Error | null>>
  setStreamingMessage: React.Dispatch<React.SetStateAction<ChatMessage | null>>
  setPendingQuickActions: React.Dispatch<React.SetStateAction<QuickAction[]>>
}

/** The caller wraps this in `useEffect(..., [sessionId, token])`. */
export function loadSessionMessages(deps: SessionMessageLoaderDeps): void {
  const {
    sessionId,
    token,
    previousSessionIdRef,
    greetingAbortControllerRef,
    greetingCancelledRef,
    greetingInProgressRef,
    bypassedNewConversationIdRef,
    lastCreatedConversationIdRef,
    clearLastCreatedConversationIdRef,
    currentSessionIdRef,
    recoverIfConversationGoneRef,
    clearActivity,
    setMessages,
    setThinking,
    setIsStreaming,
    setIsLoading,
    setError,
    setStreamingMessage,
    setPendingQuickActions,
  } = deps

  const requestedSessionId = sessionId
  const didSessionChange = previousSessionIdRef.current !== requestedSessionId
  previousSessionIdRef.current = requestedSessionId

  if (didSessionChange) {
    // Abort any in-flight greeting from the previous conversation so it can't
    // call setStreamingMessage / setIsStreaming after we've switched away.
    if (greetingAbortControllerRef.current) {
      greetingAbortControllerRef.current.abort()
      greetingAbortControllerRef.current = null
    }
    greetingCancelledRef.current = true
    greetingInProgressRef.current = false
    // Don't clear greetingLastRequestedRef — keep the dedup window active
    // so a rapid switch-back can't re-trigger a greeting
    // Reset the new-conversation bypass so a later switch back to a
    // previously-bypassed conversation loads persisted history normally.
    bypassedNewConversationIdRef.current = null
  }

  // #5194: every streaming-state reset below is gated on didSessionChange.
  // This effect re-fires on TOKEN change too (`useEffect([sessionId, token])`
  // in useChat) — apiClient's background 401→refresh rotates the token via
  // setTokenRefreshCallback mid-conversation. When the session did NOT change,
  // a re-fire must never clobber an in-flight turn: setIsStreaming(false) +
  // clearActivity() here killed the thinking indicator mid-build, and during a
  // long page build (45s+ research gaps) nothing re-asserted it — the chat
  // looked fully stalled while the build was running.

  if (!sessionId || sessionId.startsWith('conversation-')) {
    // No session or temp optimistic ID — wait for real server ID before loading
    if (didSessionChange) {
      setMessages([])
      setThinking(null)
      clearActivity()
      setIsStreaming(false)
      setError(null)
      setStreamingMessage(null)
      // Clear any quick-action chips left over from the previous conversation
      // so they don't visually persist into the new (or empty) one (#1261).
      setPendingQuickActions([])
    }
    setIsLoading(false)
    return
  }

  // Bypass the server GET for a freshly-created conversation. Otherwise the
  // GET returns [] before the server has persisted the greeting, and
  // `setMessages(deduped)` wipes the optimistic greeting ChatBox pushed.
  // The ref also matches on StrictMode's synthetic second mount after the
  // context value has been cleared on the first mount.
  const shouldBypassForNewConversation = Boolean(
    sessionId === lastCreatedConversationIdRef.current ||
    bypassedNewConversationIdRef.current === sessionId,
  )

  if (shouldBypassForNewConversation) {
    if (bypassedNewConversationIdRef.current !== sessionId) {
      bypassedNewConversationIdRef.current = sessionId
      clearLastCreatedConversationIdRef.current()
    }
    if (didSessionChange) {
      setMessages([])
      setThinking(null)
      clearActivity()
      setIsStreaming(false)
      setError(null)
      setStreamingMessage(null)
    }
    // Always end the loading state: on mount into a fresh conversation
    // (didSessionChange is false because previousSessionIdRef seeds from the
    // initial sessionId) isLoading starts true and there is no GET to end it.
    setIsLoading(false)
    return
  }

  if (didSessionChange) {
    setMessages([])
  }

  setIsLoading(true)
  getConversationChatMessages(token, sessionId, { limit: 100 })
    .then(({ messages: serverMessages }) => {
      if (currentSessionIdRef.current !== requestedSessionId) return
      const clientMessages = serverMessages
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(serverToClientMessage)
      // Deduplicate consecutive messages with same role and similar content (save retries / agent echo)
      const normalize = (s: string) => (s || '').replace(/[^a-z0-9]/gi, '').toLowerCase()
      const deduped: typeof clientMessages = []
      for (const msg of clientMessages) {
        const prev = deduped[deduped.length - 1]
        // Onboarding marker messages (the beat transcript + summary) are
        // structurally similar (same marker prefix, shared bundle JSON) and
        // MUST never be collapsed — that would wipe the onboarding chat. Always
        // keep them verbatim.
        const isMarker = (s: string) => typeof s === 'string' && s.startsWith('@@XAVIOR_ONBOARDING')
        if (isMarker(msg.content) || (prev && isMarker(prev.content))) {
          deduped.push(msg)
          continue
        }
        if (prev && prev.role === msg.role) {
          const a = normalize(prev.content)
          const b = normalize(msg.content)
          // Skip if normalized content is identical or one contains the other (minor punctuation diff)
          if (a === b || (a.length > 10 && b.length > 10 && (a.includes(b) || b.includes(a)))) continue
        }
        deduped.push(msg)
      }
      // Use functional update to avoid wiping locally-added messages (e.g. a
      // greeting injected by ChatBox while the fetch was in-flight). If the server
      // returned nothing but local messages already exist, keep them. (#1583)
      setMessages(prev => {
        if (deduped.length === 0 && prev.length > 0) return prev
        return deduped
      })
    })
    .catch((err) => {
      if (currentSessionIdRef.current !== requestedSessionId) return
      console.error('[useChat] Failed to load messages from server:', err)
      // #5232: restoring a stale last-conversation pointer lands here — the
      // conversation passed validation against a cached list but its messages
      // GET 404s. Hand the error to the recovery so the stale pointer is
      // cleared and the app re-resolves onto a real thread, instead of
      // proceeding with an empty dead thread that onboarding then seeds into.
      recoverIfConversationGoneRef?.current(err, sessionId)
      // Fall back to empty - don't use localStorage anymore
      setMessages([])
    })
    .finally(() => {
      if (currentSessionIdRef.current !== requestedSessionId) return
      setIsLoading(false)
    })

  // Streaming-state reset ONLY on a real conversation switch (#5194). A
  // token-rotation re-fire of the same session keeps the message refetch
  // above (the token dep exists so a late-arriving token can load history)
  // but must not tear down an in-flight turn's indicator/stream UI.
  if (didSessionChange) {
    setThinking(null)
    clearActivity()
    setIsStreaming(false)
    setError(null)
    setStreamingMessage(null)
    // Clear any quick-action chips left over from the previous conversation
    // so they don't visually persist into the new one (#1261). The greeting
    // effect re-evaluates against the new conversation since it depends on
    // messages length being 0.
    setPendingQuickActions([])
  }
}
